<template>
  <tr @contextmenu.prevent="emitContextMenu">
    <!-- Checkbox column -->
    <td
      v-if="isPlanning"
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer select-none z-40"
    >
      <i 
        v-if="activeState == 1" 
        class="bg-white rounded block h-4 w-4"
      ></i>
      <i
        v-else-if="activeState == 2"
        class="fas fa-check bg-blue-500 text-white rounded"
        style="padding: 2px"
      ></i>
    </td>

    <!-- Order Number Cell -->
    <td
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer select-none"
    >
      <span class="text-white text-opacity-80"># {{ item.orderNo }}</span>
      <p class="text-xs" :title="item.orderName">
        {{ truncatedOrderName }}
      </p>
    </td>

    <!-- Filling Location Cell -->
    <td
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer"
    >
      <div class="text-white" style="font-size: 10px">
        <span class="text-xs" :title="item.receivedName">
          {{ truncatedReceivedName }}
        </span>
      </div>
      <div class="flex items-center justify-between">
        <p class="text-white text-opacity-75" style="font-size: 9px">
          {{ outpointAddressWords }}
        </p>
        <p class="text-white text-opacity-50" style="font-size: 8px">
          {{ formattedOrderDate }}
        </p>
      </div>
    </td>

    <!-- Delivery Location Cell -->
    <td
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer"
    >
      <div
        class="text-white"
        style="font-size: 10px"
        :title="item.deliveredName"
      >
        {{ truncatedDeliveredName }}
      </div>
      <div class="flex items-center justify-between">
        <p class="text-white text-opacity-75" style="font-size: 8px">
          {{ targetAddressWords }}
        </p>
        <p class="text-white opacity-50" style="font-size: 8px">
          {{ formattedDeliveryDate }}
          {{ item.deliveryTime }}
        </p>
      </div>
    </td>

    <!-- Customer Cell -->
    <td
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer select-none"
    >
      <p :title="item.customer">{{ truncatedCustomer }}</p>
    </td>

    <!-- Product Type Cell -->
    <td
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer select-none"
      :title="!isProductValidResult && 'Ürün Sistemde Bulunamadı'"
      :class="!isProductValidResult && 'bg-red-700 animate-pulse'"
    >
      {{ item.productType }}
    </td>

    <!-- Quantity Cell -->
    <td
      @click="$emit('select-item')"
      class="table-td border-r border-opacity-20 cursor-pointer select-none"
    >
      <p class="text-xs">{{ item.tonnage }} {{ item.unitType }}</p>
    </td>

    <!-- Cells only visible when viewing planned orders (activeState === 2) -->
    <template v-if="!item.isMultiple && activeState === 2">
      <!-- Wash Point Cell -->
      <td
        @click="$emit('select-item')"
        class="table-td border-r border-opacity-20 cursor-pointer select-none"
      >
        <p class="text-xs">{{ item.washPointName || "-" }}</p>
      </td>

      <!-- Wash Cost Cell -->
      <td
        @click="$emit('select-item')"
        class="table-td border-r border-opacity-20 cursor-pointer select-none"
      >
        <p class="text-xs">{{ item.washCost }}</p>
      </td>

      <!-- Trip Cost Cell -->
      <td
        @click="$emit('select-item')"
        class="table-td border-r border-opacity-20 cursor-pointer select-none"
      >
        <p class="text-xs">{{ item.recomendationTotalCost }}</p>
      </td>

      <!-- Total Cost Cell -->
      <td
        @click="$emit('select-item')"
        class="table-td border-r border-opacity-20 cursor-pointer select-none"
      >
        <p class="text-xs">{{ formattedAllCost }}</p>
      </td>
    </template>

    <!-- Vehicle Cell -->
    <td
      v-if="activeState < 3"
      class="table-td w-28 border-r border-opacity-20 text-center"
      :colspan="item.isMultiple && 6"
    >
      <!-- Small order handling -->
      <div
        v-if="
          !item.isMultiple &&
          activeState == 1 &&
          isSmallOrder
        "
        class="px-4 py-1 rounded w-full text-opacity-80 text-white"
      >
        SPOT SİPARİŞ
      </div>

      <!-- Vehicle selection for planned orders -->
      <div v-else-if="!item.isMultiple && activeState == 2">
        <div 
          v-if="item.isPirateDriver==1"
          class="text-white bg-red-800 px-1 py-1 rounded text-lg m-1"
        >
          {{item.vehicle.name}}
        </div>
        <VehicleSelect
          v-else
          :value="item.vehicle"
          :optList="vehicleList"
          @changeName="handleVehicleChange"
          @changeDriverName="handleDriverChange"
          :customers="customers"
          :order="item"
          :sorted="true"
          :products="products"
          @changeDorse="val => item.dorse = val"
          @isVehicleConflictModalVisible="handleConflictModal"
          @distance-calculated="$emit('distance-calculated', $event)"
        />
      </div>

      <!-- Bulk order button -->
      <button
        v-else
        @click="showConfirm"
        class="px-4 py-1 rounded w-full text-opacity-80 text-white"
      >
        TOPLU SİPARİŞ
      </button>
    </td>

    <!-- Driver Cell -->
    <td
      v-if="activeState == 2 && !item.isMultiple"
      class="table-td border-r border-opacity-20 text-center"
    >
      <div v-if="!item.isPirateDriver">
        <PersonnelSelect
          :titleNone="true"
          :hideContainer="true"
          :value="item.vehicleName"
          @changeName="handleDriverChange"
          @changeTC="val => item.driverTC = val"
          @onChange="handlePersonnelChange"
          :isCache="true"
          class="py-2"
        />
      </div>

      <!-- Korsan sürücü için -->
      <div v-else class="text-white bg-red-800 px-2 py-1 rounded text-lg">
        {{ item.driver || item.driverName }}
        <i class="fas fa-user-secret ml-1"></i>
      </div>
    </td>

    <!-- Actions Cell -->
    <td v-if="activeState == 2" class="table-td text-center">
      <button
        class="process-btn mr-2"
        v-if="item.tonnage <= 30 && !item.isMultiple"
        @click="$emit('view-order')"
      >
        <i class="fas fa-search"></i>
      </button>
      <button class="process-btn" @click="$emit('remove-order')">
        <i class="fas fa-trash"></i>
      </button>
    </td>
  </tr>
</template>
  
<script>
import VehicleSelect from "./vehicle-select.vue";
import PersonnelSelect from "@/components/devItem/personnelInput.vue";
import similarity from "similarity";
import moment from "moment";

// Text truncation utility - bileşen dışında tanımlayarak önbelleğe almayı sağlar
const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

// Address words extraction utility
const getLastThreeWords = (address) => {
  if (!address) return "";
  const words = address.split(" ");
  if (words.length <= 3) return address;
  return words[words.length - 2] + " / " + words[words.length - 3];
};

// Product validity check cache
const productValidityCache = new Map();

export default {
  name: "TableRow",
  components: {
    VehicleSelect,
    PersonnelSelect,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    activeState: {
      type: Number,
      required: true,
    },
    isPlanning: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      required: true,
    },
    vehicleList: {
      type: Array,
      required: true,
    },
    customers: {
      type: Array,
      required: true,
    },
  },
  computed: {
    // Metin kırpma işlemleri
    truncatedOrderName() {
      return truncateText(this.item.orderName, 13);
    },
    truncatedReceivedName() {
      return truncateText(this.item.receivedName, 24);
    },
    truncatedDeliveredName() {
      return truncateText(this.item.deliveredName, 22);
    },
    truncatedCustomer() {
      return truncateText(this.item.customer, 22);
    },
    
    // Adres işlemleri
    outpointAddressWords() {
      return getLastThreeWords(this.item.outpointAdress);
    },
    targetAddressWords() {
      return getLastThreeWords(this.item.targetAdress);
    },
    
    // Tarih formatlama
    formattedOrderDate() {
      if (!this.item.orderDate) return "Belirtilmemiş";
      return moment(this.item.orderDate).format("LLL");
    },
    formattedDeliveryDate() {
      if (!this.item.deliveryDate) return "Belirtilmemiş";
      return moment(this.item.deliveryDate).format("D MMMM YYYY");
    },
    
    // Cost formatlama  
    formattedAllCost() {
      return Number(this.item.allCost || 0).toFixed(2);
    },
    
    // Ürün geçerliliği kontrolü
    isProductValidResult() {
      const productType = this.item.productType?.toString() || '';
      const cacheKey = productType.toLowerCase().replace(/\s/g, "");
      
      // Önbellekte varsa, önbellekten döndür
      if (productValidityCache.has(cacheKey)) {
        return productValidityCache.get(cacheKey);
      }
      
      // Yoksa hesapla, önbellekle ve döndür
      const findItem = this.products.find(
        (item) =>
          similarity(
            item?.name?.toString().toLowerCase().replace(/\s/g, ""),
            cacheKey
          ) > 0.95
      );

      let result = false;
      if (findItem) {
        result = Array.isArray(findItem.dorseTypes) 
          ? findItem.dorseTypes.length > 0 
          : true;
      }
      
      productValidityCache.set(cacheKey, result);
      return result;
    },
    
    // Küçük sipariş kontrolü
    isSmallOrder() {
      return this.item.unitType === 'TON' 
        ? this.item.tonnage < 31 
        : this.item.tonnage < 2;
    }
  },
  methods: {
    // Event handler fonksiyonları
    emitContextMenu(event) {
      this.$emit('open-context-menu', event);
    },
    handleVehicleChange(val, isManualTyped = false) {
      this.$emit('change-vehicle', val, isManualTyped);
    },
    handleDriverChange(driver) {
      this.$emit('change-driver', driver);
    },
    handlePersonnelChange(val) {
      this.$emit('handle-personel-change', val, this.item);
    },
    handleConflictModal(val) {
      this.$emit('isVehicleConflictModalVisible', val);
    },
    showConfirm() {
      this.$swal({
        title: "UYARI",
        text: "BU SİPARİŞ KAYIT EDİLDİKTEN SONRA TOPLU PLANLAMA EKRANINDAN PLANLANABİLİR!",
        icon: "warning",
        confirmButtonText: "Tamam",
      });
    }
  },
  // Bileşen oluşturulduğunda product cache'i temizle
  created() {
    // Her yeni satır oluşturulduğunda önbelleği temizleme
    // Bu, uzun süre açık kalan uygulamalarda bellek sızıntısını önler
    if (productValidityCache.size > 1000) {
      productValidityCache.clear();
    }
  }
};
</script>